<script lang="ts">
    import { Subscription, fromEvent } from "rxjs";
    import {
        debounceTime,
        distinctUntilChanged,
        filter,
        tap,
    } from "rxjs/operators";
    import { onMount } from "svelte";

    import type SliderControllerInterface from "./SliderControllerInterface";
    import type SliderPerPageInterface from "./SliderPerPageInterface";

    export let perPage: number | SliderPerPageInterface = 3;
    export let loop = true;
    export let duration = 200;
    export let easing = "ease-out";
    export let startIndex = 0;
    export let draggable = true;
    export let multipleDrag = true;
    export let controls = true;
    export let threshold = 20;
    export let sliderController: SliderControllerInterface;
    export let slidesCount = 0;
    export let autoHeight = false;
    export let currentIndex = 0;
    let sliderElement: HTMLElement | null = null;
    let resizeSub: Subscription | null = null;
    let options = {
        perPage,
        loop,
        duration,
        easing,
        startIndex,
        draggable,
        multipleDrag,
        threshold,
    };
    const updateHeight = () => {
        if (autoHeight) {
            const elements = sliderController.getSliderElements() || [];
            if (sliderElement) {
                sliderElement.style.height = `${elements[currentIndex].scrollHeight}px`;
            }
        }
    };
    onMount(() => {
        let currentIndexSub: Subscription | null = null;
        if (sliderElement) {
            sliderController.connect(sliderElement, options);
            if (autoHeight) {
                currentIndexSub = sliderController
                    .selectCurrentSlideIndex()
                    .pipe(
                        filter((v: number | null): v is number => v !== null),
                        debounceTime(500),
                        distinctUntilChanged(),
                        tap(() => {
                            updateHeight();
                        })
                    )
                    .subscribe();
                resizeSub = fromEvent(window, "resize")
                    .pipe(
                        debounceTime(500),
                        distinctUntilChanged(),
                        tap(() => {
                            updateHeight();
                        })
                    )
                    .subscribe();
            }
            return () => {
                sliderController.destroy();
                currentIndexSub?.unsubscribe();
                resizeSub?.unsubscribe();
            };
        }
    });
</script>

<div class="gw-slider">
    <div
        class="gw-slider__slides"
        class:gw-slider__slides--autoHeight={autoHeight}
        bind:this={sliderElement}
    >
        <slot name="slides" {updateHeight} />
    </div>
    {#if controls && slidesCount !== perPage}
        <slot name="arrows" />
    {/if}
    <slot name="dots" />
</div>

<style>
    .gw-slider {
        position: relative;
        width: 100%;
    }

    .gw-slider__slides {
        width: 100%;
    }

    .gw-slider__slides--autoHeight {
        height: 0;
        transition: height 0.2s ease-in-out;
    }
</style>
